import { useContext, useState, useRef, useEffect, useCallback } from 'react';
import { SmartContext } from '../../../library/Core/SmartContext';
import SessionContext from '../../../library/Core/SessionContext';
import { allowPerformAction, displayActionNotAllowedAlert, getActionElementClass, isEmpty } from '../../../library/Core/SmartFunctions';
import useSmartState from '../../../common/hooks/useSmartState';
import AiSuggestedService from './AiSuggestedService';
import AddServiceProviderModal from './AddServiceProviderModal';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { getStudentImage } from '../../../common/styles/Images';

const AiSuggestedResults = () => {
    const { state } = useContext(SmartContext);
    const { setStateValue } = useSmartState();
    const { sessionState } = useContext(SessionContext);
    const isAlumni = sessionState?.userTypeCode === 'ALUMNI';

    const [approveData, setApproveData] = useState<any>(null);
    const [rejectingId, setRejectingId] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const sentinelRef = useRef<HTMLDivElement>(null);

    const providerList = state.data?.providerList ?? [];
    const pagination = state.data?.pagination ?? { total: 0, page: 1, limit: 20 };
    const hasMore = state.data?.hasMore ?? false;
    const searchCriteria = state.data?.searchCriteria ?? {};

    // AI Confidence color coding per ticket:
    // 90-100%: green | 75-89%: blue | 60-74%: yellow | <60%: gray
    const getConfidenceBadgeStyle = (confidence: number) => {
        const score = Number(confidence) || 0;
        if (score >= 90) return { bg: '#d1fae5', color: '#059669' };
        if (score >= 75) return { bg: '#dbeafe', color: '#1e40af' };
        if (score >= 60) return { bg: '#fef3c7', color: '#d97706' };
        return { bg: '#f3f4f6', color: '#6b7280' };
    };

    const loadMore = useCallback(async () => {
        if (loadingMore || !hasMore) return;
        setLoadingMore(true);
        try {
            const nextPage = (pagination.page ?? 1) + 1;
            const criteria = { ...searchCriteria, page: nextPage };
            const response = await AiSuggestedService.search(criteria);
            const payload = response?.data ?? {};
            const newItems = payload?.data ?? [];
            const newPage = payload?.page ?? nextPage;
            const newTotal = payload?.total ?? pagination.total;
            const newLimit = payload?.limit ?? pagination.limit;

            setStateValue('providerList', [...providerList, ...newItems] as any);
            setStateValue('pagination', { total: newTotal, page: newPage, limit: newLimit } as any);
            setStateValue('hasMore', (newPage * newLimit < newTotal) as any);
            setStateValue('searchCriteria', { ...searchCriteria, page: newPage } as any);
        } catch (err) {
            console.error('Load more error:', err);
            toast.error('Failed to load more suggestions');
        } finally {
            setLoadingMore(false);
        }
    }, [loadingMore, hasMore, pagination, providerList, searchCriteria, setStateValue]);

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    loadMore();
                }
            },
            { threshold: 0.1 }
        );
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loadMore]);

    const refreshList = () => {
        const resetCriteria = { ...(state.data?.searchCriteria || {}), page: 1 };
        AiSuggestedService.search(resetCriteria).then((response: any) => {
            const payload = response?.data ?? {};
            const total = payload?.total ?? 0;
            const limit = payload?.limit ?? 20;
            setStateValue('providerList', payload?.data ?? [] as any);
            setStateValue('pagination', { total, page: payload?.page ?? 1, limit } as any);
            setStateValue('hasMore', ((payload?.page ?? 1) * limit < total) as any);
            setStateValue('searchCriteria', resetCriteria as any);
        });
    };

    const handleReject = async (provider: any) => {
        if (!allowPerformAction()) {
            displayActionNotAllowedAlert();
            return;
        }
        const confirm = await Swal.fire({
            icon: 'warning',
            title: 'Reject Suggestion?',
            text: 'Are you sure you want to reject this suggestion? This alumnus will not be suggested again.',
            showCancelButton: true,
            confirmButtonText: 'Reject',
            confirmButtonColor: '#dc2626',
            cancelButtonText: 'Cancel',
        });
        if (!confirm.isConfirmed) return;

        try {
            setRejectingId(String(provider.providerId));
            await AiSuggestedService.rejectSuggestion(provider.providerId);
            toast.success('Suggestion rejected');
            setTimeout(() => {
                setRejectingId(null);
                refreshList();
            }, 350);
        } catch (err) {
            console.error('Reject error:', err);
            setRejectingId(null);
            toast.error('Failed to reject suggestion');
        }
    };

    const handleApprove = (provider: any) => {
        if (!allowPerformAction()) {
            displayActionNotAllowedAlert();
            return;
        }
        setApproveData({
            id: provider.providerId,
            userId: provider.userId,
            providerName: provider.alumniName,
            programName: provider.programName,
            passOutYear: provider.passOutYear,
            imagePath: provider.imagePath,
            categoryCode: provider.category,
            subcategoryCode: provider.subcategory,
            businessName: provider.businessName,
            country: provider.country,
            city: provider.city,
            experienceYears: provider.experience,
            availabilityStatus: provider.availability,
            websiteUrl: provider.websiteUrl,
            instituteId: provider.instituteId,
            isAiSuggested: true,
        });
    };

    const renderEmptyState = () => (
        <div className="empty-state text-center p-5">
            <i className="bi bi-robot font-48 text-muted d-block mb-3"></i>
            <h5 className="font-18 font-600 mb-2">No AI suggestions available at this time.</h5>
            <p className="text-muted mb-0">
                Our AI system periodically analyzes alumni profiles to identify potential service providers.
                Check back later for new suggestions.
            </p>
        </div>
    );

    return (
        <div className="results-section">
            <div className="results-header d-flex justify-content-between align-items-center mb-4 gap-3">
                <div className="results-count">
                    <span className="text-muted">
                        Showing {providerList.length} of {pagination.total} suggestion{pagination.total !== 1 ? 's' : ''}
                    </span>
                </div>
            </div>

            {/* Info banner per ticket */}
            <div className="alert alert-info mb-4" role="alert">
                <h6 className="font-16 font-600 mb-1">Recommended Service Providers</h6>
                <p className="font-14 mb-0">
                    These alumni have been identified by our system as potential service providers based on their work
                    experience and profile information. Review and approve them to add to the Business Directory.
                </p>
            </div>

            {/* Cards grid — same layout as Business Directory grid view */}
            <div className="results-body">
                {isEmpty(providerList) ? (
                    renderEmptyState()
                ) : (
                    <div className="row g-4">
                        {providerList.map((provider: any, index: number) => {
                            const isRejecting = rejectingId === String(provider.providerId);
                            const confidenceStyle = getConfidenceBadgeStyle(provider.aiConfidence);
                            return (
                                <div
                                    key={`${provider.providerId}-${index}`}
                                    className="col-sm-6 col-md-6"
                                    style={{
                                        opacity: isRejecting ? 0 : 1,
                                        transform: isRejecting ? 'scale(0.95)' : 'scale(1)',
                                        transition: 'opacity 300ms ease, transform 300ms ease',
                                    }}>
                                    <div className="business-card border-radius-6 h-100 d-flex flex-column border-0B3454 overflow-hidden">
                                        {/* Card header — alumni photo + name + graduation + AI confidence */}
                                        <div className="bg-darker-blue text-white p-4">
                                            <div className="row g-2 align-items-center">
                                                <div className="col">
                                                    <div className='row g-3 align-items-center'>
                                                        <div className="col-auto">
                                                            <div className="alumniDetailPic ratio-60 position-relative d-flex justify-content-center align-items-center">
                                                                <img
                                                                    src={getStudentImage({ profilePictureFilePath: provider.imagePath, genderCode: provider.genderCode })}
                                                                    className="rounded-circle"
                                                                    width="60"
                                                                    height="60"
                                                                    alt={provider.alumniName}
                                                                    style={{ objectFit: 'cover' }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="col">
                                                            <div className="d-flex flex-column">
                                                                <a
                                                                    href={provider.userId ? `/alumni/${provider.userId}/profile` : '#'}
                                                                    target="_blank"
                                                                    rel="noopener noreferrer"
                                                                    className="font-22 font-500 line-height-24 text-break text-white text-decoration-none"
                                                                    style={{ cursor: 'pointer' }}
                                                                    onClick={(e) => {
                                                                        if (!provider.userId) e.preventDefault();
                                                                    }}>
                                                                    {provider.alumniName}
                                                                </a>
                                                                <span className="font-16 font-400">
                                                                    {[provider.programName, provider.passOutYear]
                                                                        .filter(Boolean)
                                                                        .join(', ') || '-'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                </div>
                                                <div className="col-lg-auto">
                                                    {/* AI Confidence badge — color-coded per ticket */}
                                                    <div className="d-flex mt-2">
                                                        <div
                                                            className="rounded-pill px-3 py-2 d-flex align-items-center"
                                                            style={{
                                                                backgroundColor: confidenceStyle.bg,
                                                                color: confidenceStyle.color,
                                                            }}>
                                                            <i className="bi bi-stars me-1 font-20"></i>
                                                            <span className="font-20 font-600 me-1">
                                                                {provider.aiConfidence ?? 0}%
                                                            </span>
                                                            <span className="font-14">AI Confidence</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                        </div>

                                        {/* Card body — service info from ai_suggestions + alumni tables */}
                                        <div className="flex-1 p-4">
                                            <div className="row g-4">
                                                <div className="col-sm-6">
                                                    <span className="font-16 font-400">Service Category</span>
                                                    <p className="font-16 font-400 lh-sm mb-0">
                                                        <b className="d-block font-500">{provider.category || '-'}</b>
                                                        {provider.subcategory || ''}
                                                    </p>
                                                </div>

                                                <div className="col-sm-6">
                                                    <span className="font-16 font-400">Current Organization</span>
                                                    <p className="font-16 font-400 lh-sm mb-0">
                                                        <b className="d-block font-500">{provider.businessName || '-'}</b>
                                                    </p>
                                                </div>

                                                <div className="col-sm-6">
                                                    <span className="font-16 font-400">Location</span>
                                                    <p className="font-16 font-400 lh-sm mb-0">
                                                        <b className="d-block font-500">
                                                            {[provider?.cityName, provider?.countryName]
                                                                .filter(Boolean)
                                                                .join(', ') || '-'}
                                                        </b>
                                                    </p>
                                                </div>

                                                {/* AI Identification Reason — only if available */}
                                                {provider.aiReason && (
                                                    <div className="col-sm-12">
                                                        <div className="bg-blue-eff8ff p-3 border-radius-6">
                                                            <b className="font-500 d-block mb-1">
                                                                <i className="bi bi-stars me-1"></i> AI Identification Reason
                                                            </b>
                                                            <span>{provider.aiReason}</span>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Action buttons */}
                                                <div className="col-sm-12">
                                                    <div className="row g-2 justify-content-center align-items-center">
                                                        {!isAlumni && (
                                                            <>
                                                                <div className="col-auto">
                                                                    <button
                                                                        className={`btn btn-border-gray height-40 ${getActionElementClass()}`}
                                                                        onClick={() => handleReject(provider)}>
                                                                        <span>Reject</span>
                                                                    </button>
                                                                </div>
                                                                <div className="col-auto">
                                                                    <button
                                                                        className={`btn btn-green text-white height-40 px-3 ${getActionElementClass()}`}
                                                                        onClick={() => handleApprove(provider)}>
                                                                        <span>Approve & Add to Directory</span>
                                                                    </button>
                                                                </div>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Infinite scroll sentinel — triggers loadMore when scrolled into view */}
            <div ref={sentinelRef} className="py-3 text-center">
                {loadingMore && (
                    <div className="spinner-border text-primary" role="status" style={{ width: '1.5rem', height: '1.5rem' }}>
                        <span className="visually-hidden">Loading more...</span>
                    </div>
                )}
            </div>

            {/* Approve modal — opens AddServiceProviderModal in approve mode (consent NOT pre-checked) */}
            {approveData && (
                <AddServiceProviderModal
                    approveData={approveData}
                    onEditClose={() => {
                        setApproveData(null);
                        refreshList();
                    }}
                />
            )}
        </div>
    );
};

export default AiSuggestedResults;
